import { chatPrefillRoute } from './piAssistantModel.js';
import { defineColumns } from './piColumns.js';
import { EMPTY, formatDate, formatDateFull, formatDelta, formatInteger, formatPercent, formatTime, isNumber, toInstant } from './piFormat.js';
import { PI_BASE_PATH, metricKeyOfSegment, metricPathSegment, viewPath } from './piNavigation.js';
import { JOURNEY_OPTIONS, SIZE_BAND_FILTER_OPTIONS, journeyLabel, optionLabel } from './piQuery.js';
import { listOf, objectOf } from './piRequests.js';
import { stateLabel, stateTone } from './piState.js';
import { stateInfoKey } from './glossary/index.js';

export const AREAS_PATH = PI_BASE_PATH + '/areas';

export const AREA_METRIC_PATH = AREAS_PATH + '/metricas/';

export const AREA_KEYS = ['journey', 'people_development', 'communication', 'culture', 'hr_documents', 'recruitment'];

export const AREA_LABELS = {
  journey: 'Ponto e Jornada',
  people_development: 'Desenvolvimento de Pessoas',
  communication: 'Comunicação Interna',
  culture: 'Endomarketing, Clima e Benefícios',
  hr_documents: 'DP, Documentos e Assinaturas',
  recruitment: 'Recrutamento e Seleção',
};

export const AREA_FILTER_FIELDS = ['sizeBand', 'segment'];

export const AREA_QUERY_KEYS = ['area', 'journey'];

export const AREA_TRIAL_OPTIONS = [
  { value: 'exclude', label: 'Sem as empresas em trial' },
  { value: 'only', label: 'Só as empresas em trial' },
];

export const AREA_TRIAL_ALL_LABEL = 'Com e sem trial';

export const AREA_FILTER_DEFAULTS = { sizeBand: '', segment: '', journey: '', trial: '' };

export const AREAS_NOT_STARTED = 'As áreas começam a ser medidas na próxima madrugada depois de ligar job:pa-snapshot:areas.';

export const AREA_SHORT_LIST = 4;

export const AREA_COMPANY_PAGE_SIZE = 25;

export const DEFAULT_AREA_COMPANY_SORT = { sort: 'value', desc: true };

export const SENSITIVE_MINIMUM = 5;

export const AREA_SCREEN = 'Áreas';

export const NOT_STARTED_NOTE_PREFIX = 'Sem retrato das áreas:';

export const COMPANIES_NOTE_TEXTS = ['Empresas do recorte com valor', 'ficaram fora do corte por empresa'];

export const SHARED_NOTE_PREFIXES = [
  'Estoque do retrato noturno',
  'Empresas que contrataram:',
  'Variação de 7 e 28 dias',
  'Sem as empresas em teste.',
  'Só as empresas em teste.',
];

export const STOCK_LEAD = 'Estoque de cada área do produto no retrato noturno dos bancos dos clientes: o que existe hoje (em andamento, pendente, '
  + 'aberto, atrasado), contado por empresa. Não é uso: o uso vem dos eventos, nas outras abas.';

const COVERAGE_STATUS = {
  ok: { label: 'Lido', tone: 'ok' },
  partial: { label: 'Lido sem índice', tone: 'warn' },
  failed: { label: 'Falhou', tone: 'bad' },
  stale: { label: 'Desatualizado', tone: 'warn' },
  missing: { label: 'Sem leitura', tone: 'muted' },
};

const METRIC_STATES = ['ok', 'partial', 'na'];

const SIZE_BAND = /^[A-E]$/;

const METRIC_KEY = /^[a-z][a-z0-9_]*(\.[a-z0-9_]+)+$/;

const MAX_METRIC_KEY = 60;

const CODE_REF = /\.(?:cs|tsx|ts|js|vue|sql)\b|:\d/;

const AREA_HOW = 'Retrato noturno: às 23:30 (horário de Brasília) o PI lê cada banco de clientes e grava a contagem de cada empresa. '
  + 'Entram só empresas ativas e não excluídas, fora as de demonstração e o ambiente de Portugal.';

const METRIC_HOW = 'Retrato noturno dos bancos dos clientes às 23:30 (horário de Brasília), só de empresas ativas do universo do PI.';

const textOf = (value) => (value === null || value === undefined ? '' : String(value).trim());

const firstOf = (value) => (Array.isArray(value) ? value[0] : value);

const numberOrNull = (value) => (isNumber(value) ? value : null);

const allowed = (options, value) => options.some((option) => option.value === value);

const lowerFirst = (text) => (text ? text.charAt(0).toLowerCase() + text.substring(1) : '');

const sentence = (text) => {
  const clean = textOf(text);
  if (!clean) return '';
  return /[.!?]$/.test(clean) ? clean : clean + '.';
};

const plural = (count, one, many) => formatInteger(count) + ' ' + (count === 1 ? one : many);

export const withoutCodeRefs = (value) => textOf(value)
  .replace(/\s*\(([^()]*)\)/g, (match, inner) => {
    if (!CODE_REF.test(inner)) return match;
    return /^legado\b/i.test(inner.trim()) ? ' (legado)' : '';
  })
  .replace(/\s+([.,;:])/g, '$1')
  .replace(/\s{2,}/g, ' ')
  .trim();

export const cleanAreaFilters = (value) => {
  const source = objectOf(value);
  const sizeBand = textOf(source.sizeBand);
  const journey = textOf(source.journey);
  const trial = textOf(source.trial);
  return {
    sizeBand: SIZE_BAND.test(sizeBand) ? sizeBand : '',
    segment: textOf(source.segment).replace(/\s+/g, ' ').substring(0, 60),
    journey: allowed(JOURNEY_OPTIONS, journey) ? journey : '',
    trial: allowed(AREA_TRIAL_OPTIONS, trial) ? trial : '',
  };
};

export const areaFiltersOf = (filters, journey) => {
  const source = objectOf(filters);
  return cleanAreaFilters({ sizeBand: source.sizeBand, segment: source.segment, trial: source.trial, journey: journey });
};

export const sharedFilterPatch = (filters, next) => {
  const current = areaFiltersOf(filters, '');
  const clean = cleanAreaFilters(next);
  const patch = {};
  ['sizeBand', 'segment', 'trial'].forEach((key) => {
    if (clean[key] !== current[key]) patch[key] = clean[key];
  });
  return patch;
};

export const hasAreaFilters = (value) => {
  const clean = cleanAreaFilters(value);
  return !!(clean.sizeBand || clean.segment || clean.journey || clean.trial);
};

export const areaParams = (value, extra) => {
  const clean = cleanAreaFilters(value);
  const params = {};
  if (clean.sizeBand) params.sizeBand = clean.sizeBand;
  if (clean.segment) params.segment = clean.segment;
  if (clean.journey) params.journey = clean.journey;
  if (clean.trial) params.trial = clean.trial;
  const more = objectOf(extra);
  Object.keys(more).forEach((key) => {
    const item = more[key];
    if (item === '' || item === null || item === undefined) return;
    params[key] = typeof item === 'boolean' ? (item ? 'true' : 'false') : String(item);
  });
  return params;
};

export const areaQueryState = (query) => {
  const source = objectOf(query);
  const area = textOf(firstOf(source.area));
  const journey = textOf(firstOf(source.journey));
  return {
    present: AREA_QUERY_KEYS.some((key) => textOf(firstOf(source[key])) !== ''),
    area: AREA_KEYS.indexOf(area) !== -1 ? area : '',
    journey: allowed(JOURNEY_OPTIONS, journey) ? journey : '',
  };
};

export const withoutAreaQuery = (query) => {
  const source = objectOf(query);
  const next = {};
  Object.keys(source).forEach((key) => {
    if (AREA_QUERY_KEYS.indexOf(key) === -1) next[key] = source[key];
  });
  return next;
};

export const parseMetricKey = (value) => {
  const key = metricKeyOfSegment(textOf(firstOf(value)));
  return key.length <= MAX_METRIC_KEY && METRIC_KEY.test(key) ? key : '';
};

export const metricPath = (metricKey) => {
  const key = parseMetricKey(metricKey);
  return key ? AREA_METRIC_PATH + encodeURIComponent(metricPathSegment(key)) : '';
};

const carriedQuery = (query, journey, area) => {
  const next = withoutAreaQuery(query);
  if (allowed(JOURNEY_OPTIONS, textOf(journey))) next.journey = textOf(journey);
  if (area && AREA_KEYS.indexOf(area) !== -1) next.area = area;
  return next;
};

export const areasRoute = (query, journey, area) => ({ path: viewPath('areas'), query: carriedQuery(query, journey, area) });

export const metricRoute = (metricKey, query, journey) => {
  const path = metricPath(metricKey);
  if (!path) return null;
  return { path: path, query: carriedQuery(query, journey, '') };
};

export const cutTexts = (value) => {
  const clean = cleanAreaFilters(value);
  const parts = [];
  if (clean.sizeBand) parts.push(lowerFirst(optionLabel(SIZE_BAND_FILTER_OPTIONS, clean.sizeBand)));
  if (clean.segment) parts.push('perfil da empresa ' + clean.segment);
  if (clean.journey) parts.push('jornada ' + journeyLabel(clean.journey));
  if (clean.trial) parts.push(lowerFirst(optionLabel(AREA_TRIAL_OPTIONS, clean.trial)));
  return parts;
};

const withCut = (question, filters) => {
  const parts = cutTexts(filters);
  return parts.length ? question + ' Recorte: ' + parts.join(', ') + '.' : question;
};

export const metricQuestion = (label, filters) => withCut('Quanto há hoje de "' + textOf(label)
  + '", em quantas empresas e entre as que contrataram, e como variou em 7 e 28 dias?', filters);

export const areaQuestion = (areaLabel, filters) => withCut('Como está o estoque de ' + textOf(areaLabel)
  + ' hoje? O que mais cresceu ou caiu em 7 dias e em quais empresas?', filters);

export const askRoute = (areaKey, question) => chatPrefillRoute(AREA_KEYS.indexOf(areaKey) !== -1 ? areaKey : '', textOf(question));

export const areaFilterLines = (value) => {
  const clean = cleanAreaFilters(value);
  const lines = [];
  if (clean.journey) lines.push('Jornada: ' + journeyLabel(clean.journey));
  lines.push('Trial: ' + (clean.trial ? optionLabel(AREA_TRIAL_OPTIONS, clean.trial) : AREA_TRIAL_ALL_LABEL));
  return lines;
};

export const snapshotLine = (snapshotDate, asOf) => {
  const date = formatDateFull(snapshotDate);
  if (date === EMPTY) return '';
  const instant = toInstant(asOf);
  return 'Retrato das áreas: ' + date + (instant ? ' (lido em ' + formatDateFull(instant) + ' às ' + formatTime(instant) + ')' : '');
};

export const asOfText = (value) => {
  const instant = toInstant(value);
  return instant ? formatDate(instant) + ' às ' + formatTime(instant) : '';
};

export const areaEnvelope = (envelope, hiddenNotes) => {
  if (!envelope || typeof envelope !== 'object') return null;
  const next = Object.assign({}, envelope, { coverage: null });
  const hidden = listOf(hiddenNotes);
  if (hidden.length) {
    next.notes = listOf(envelope.notes).filter((note) => !hidden.some((text) => String(note).indexOf(text) !== -1));
  }
  return next;
};

export const metricLabels = (areas) => {
  const labels = {};
  listOf(areas).forEach((area) => {
    listOf(objectOf(area).metrics).forEach((metric) => {
      const source = objectOf(metric);
      const key = textOf(source.key);
      if (key && textOf(source.label)) labels[key] = textOf(source.label);
    });
  });
  return labels;
};

const partsText = (keys, labels) => listOf(keys)
  .map((key) => textOf(objectOf(labels)[key]) || textOf(key))
  .filter((text) => !!text)
  .join(' + ');

export const rateFormula = (rate, labels) => {
  const source = objectOf(rate);
  const numerator = partsText(source.numeratorKeys, labels);
  const denominator = partsText(source.denominatorKeys, labels);
  return numerator && denominator ? numerator + ' ÷ ' + denominator + ' × 100' : '';
};

export const deltaText = (value, previous, pct) => {
  if (!isNumber(value) || !isNumber(previous)) return '';
  return formatDelta(value - previous, isNumber(pct) ? pct : null, 'count');
};

export const reachText = (companies, eligible, share, hasFeature) => {
  if (!isNumber(companies)) return '';
  const base = companies === 0 ? 'em nenhuma empresa' : 'em ' + plural(companies, 'empresa', 'empresas');
  if (!hasFeature) return base;
  if (isNumber(share)) return base + ' (' + formatPercent(share) + ' das que contrataram)';
  if (eligible === 0) return base + ' (nenhuma empresa do recorte contratou)';
  return base;
};

const SENSITIVE_CAVEAT = 'Métrica sensível: empresa com menos de ' + SENSITIVE_MINIMUM
  + ' na base não aparece na lista por empresa nem nos cortes por porte e perfil; no total, conta sempre.';

const NO_FEATURE_CAVEAT = 'Não depende de funcionalidade contratada, então não há empresas que contrataram nem participação.';

export const metricEntry = (metric) => {
  const source = objectOf(metric);
  const label = textOf(source.label);
  const screen = withoutCodeRefs(source.screen);
  const caveats = [];
  if (source.sensitive === true) caveats.push(SENSITIVE_CAVEAT);
  if (source.featureType === null || source.featureType === undefined) caveats.push(NO_FEATURE_CAVEAT);
  const entry = {
    label: label,
    whatIs: withoutCodeRefs(source.definition),
    purpose: 'Dimensiona quanto existe hoje de "' + label + '" no recorte e em quantas empresas; o detalhe mostra a série, os cortes e as empresas com mais.'
      + (source.primary === true && textOf(source.areaLabel) ? ' É a métrica principal de ' + textOf(source.areaLabel) + '.' : ''),
    howGenerated: METRIC_HOW + (screen ? ' Na empresa, segue a regra de: ' + sentence(lowerFirst(screen)) : ''),
  };
  if (caveats.length) entry.caveat = caveats.join(' ');
  if (textOf(source.formula)) entry.formula = textOf(source.formula);
  return entry;
};

const expertiseTopics = (expertise) => textOf(expertise).replace(/^Especialista em\s+/i, '').replace(/\.$/, '');

export const areaEntry = (area) => {
  const source = objectOf(area);
  const label = textOf(source.label) || AREA_LABELS[textOf(source.key)] || 'Área';
  const topics = expertiseTopics(source.expertise);
  const entry = {
    label: label,
    whatIs: topics ? 'Área do produto que reúne ' + sentence(topics) : 'Área do produto ' + label + '.',
    purpose: 'Mostra o estoque da área no total e por empresa; as dúvidas sobre esses números vão para o especialista em ' + label + ' no Chat.',
    howGenerated: AREA_HOW,
  };
  if (textOf(source.limits)) entry.caveat = sentence(source.limits);
  return entry;
};

const coverageStatus = (status) => (Object.prototype.hasOwnProperty.call(COVERAGE_STATUS, status) ? status : 'missing');

export const coverageCell = (raw, labels) => {
  const source = objectOf(raw);
  const names = objectOf(labels);
  const status = coverageStatus(textOf(source.status));
  const info = COVERAGE_STATUS[status];
  const blocked = listOf(source.blockedMetrics).map(textOf).filter((key) => !!key);
  const measuredFor = formatDateFull(source.measuredFor);
  const lastRun = toInstant(source.lastRunUtc);
  const error = textOf(source.error);
  let detail = '';
  if (status === 'partial' && blocked.length) detail = blocked.map((key) => textOf(names[key]) || key).join(', ');
  if (status === 'stale' && measuredFor !== EMPTY) detail = 'retrato de ' + measuredFor;
  if (status === 'failed' && error) detail = error;
  const details = [];
  if (measuredFor !== EMPTY) details.push('Retrato lido: ' + measuredFor);
  if (lastRun) details.push('última execução em ' + formatDateFull(lastRun) + ' às ' + formatTime(lastRun));
  if (error && status !== 'failed') details.push(error);
  return {
    status: status,
    label: info.label,
    tone: info.tone,
    detail: detail,
    text: info.label + (detail ? ': ' + detail : ''),
    title: details.join(' · '),
    blocked: blocked,
  };
};

const MISSING_CELL = coverageCell({ status: 'missing' });

export const coverageSummary = (raw, labels) => {
  const source = objectOf(raw);
  const rows = listOf(source.rows).map((row) => {
    const item = objectOf(row);
    return Object.assign({ shardCode: textOf(item.shardCode).toUpperCase(), companies: numberOrNull(item.companies) || 0 }, coverageCell(item, labels));
  });
  const shards = numberOrNull(source.shards) || 0;
  const measured = numberOrNull(source.measured) || 0;
  const blocked = rows.filter((row) => row.companies > 0 && row.blocked.length > 0).length;
  let text = '';
  if (shards > 0) {
    text = 'Bancos lidos: ' + formatInteger(measured) + ' de ' + formatInteger(shards);
    if (blocked) text += ', ' + (blocked === 1 ? '1 com métrica sem índice' : formatInteger(blocked) + ' com métricas sem índice');
  }
  return {
    shards: shards,
    measured: measured,
    partial: source.isPartial === true,
    text: text,
    rows: rows,
  };
};

export const badgeInfoKey = (state) => (state === 'partial' ? 'areas.partial' : stateInfoKey(state));

export const metricRow = (raw, area, labels) => {
  const source = objectOf(raw);
  const areaInfo = objectOf(area);
  const areaKey = textOf(areaInfo.key) || textOf(source.area);
  const areaLabel = textOf(areaInfo.label) || AREA_LABELS[areaKey] || areaKey;
  const key = textOf(source.key);
  const label = textOf(source.label) || key;
  const featureType = isNumber(source.featureType) ? source.featureType : null;
  const hasFeature = featureType !== null;
  const value = numberOrNull(source.value);
  const companies = numberOrNull(source.companies);
  const eligibleCompanies = hasFeature ? numberOrNull(source.eligibleCompanies) : null;
  const companiesWithFeature = hasFeature ? numberOrNull(source.companiesWithFeature) : null;
  const share = hasFeature ? numberOrNull(source.share) : null;
  const previous7 = numberOrNull(source.previous7);
  const previous28 = numberOrNull(source.previous28);
  const rateSource = source.rate && typeof source.rate === 'object' ? source.rate : null;
  const rate = rateSource ? {
    numerator: numberOrNull(rateSource.numerator),
    denominator: numberOrNull(rateSource.denominator),
    value: numberOrNull(rateSource.value),
    numeratorKeys: listOf(rateSource.numeratorKeys).map(textOf),
    denominatorKeys: listOf(rateSource.denominatorKeys).map(textOf),
    formula: rateFormula(rateSource, labels),
  } : null;
  const state = METRIC_STATES.indexOf(source.state) !== -1 ? source.state : (value === null ? 'na' : 'ok');
  const stateReason = textOf(source.stateReason);
  const row = {
    key: key,
    areaKey: areaKey,
    areaLabel: areaLabel,
    label: label,
    definition: withoutCodeRefs(source.definition),
    screen: withoutCodeRefs(source.screen),
    featureType: featureType,
    hasFeature: hasFeature,
    primary: source.primary === true,
    sensitive: source.sensitive === true,
    value: value,
    companies: companies,
    eligibleCompanies: eligibleCompanies,
    companiesWithFeature: companiesWithFeature,
    share: share,
    previous7: previous7,
    previous28: previous28,
    deltaPct7: numberOrNull(source.deltaPct7),
    deltaPct28: numberOrNull(source.deltaPct28),
    rate: rate,
    asOf: textOf(source.asOf) || null,
    state: state,
    stateLabel: stateLabel(state),
    stateReason: stateReason,
    badge: state === 'ok' ? null : { label: stateLabel(state), tone: stateTone(state), title: stateReason, infoKey: badgeInfoKey(state) },
    valueText: value === null ? EMPTY : formatInteger(value),
    companiesText: companies === null ? EMPTY : formatInteger(companies),
    reachText: reachText(companies, eligibleCompanies, share, hasFeature),
    shareText: share === null ? EMPTY : formatPercent(share),
    shareSub: companiesWithFeature !== null && eligibleCompanies !== null
      ? formatInteger(companiesWithFeature) + ' de ' + formatInteger(eligibleCompanies) : '',
    delta7: deltaText(value, previous7, source.deltaPct7),
    delta28: deltaText(value, previous28, source.deltaPct28),
    rateText: rate && rate.value !== null ? formatPercent(rate.value) : '',
    rateSub: rate && rate.numerator !== null && rate.denominator !== null
      ? formatInteger(rate.numerator) + ' de ' + formatInteger(rate.denominator) : '',
  };
  row.entry = metricEntry({
    label: label,
    definition: source.definition,
    screen: source.screen,
    sensitive: row.sensitive,
    featureType: featureType,
    primary: row.primary,
    areaLabel: areaLabel,
    formula: rate ? rate.formula : '',
  });
  return row;
};

const withLinks = (row, query, journey) => Object.assign(row, { route: metricRoute(row.key, query, journey) });

export const areaCard = (raw, labels, options) => {
  const source = objectOf(raw);
  const settings = objectOf(options);
  const key = textOf(source.key);
  const label = textOf(source.label) || AREA_LABELS[key] || key;
  const metrics = listOf(source.metrics).map((item) => withLinks(metricRow(item, { key: key, label: label }, labels), settings.query, settings.journey));
  const primary = metrics.filter((metric) => metric.primary)[0] || metrics[0] || null;
  const others = metrics.filter((metric) => metric !== primary);
  return {
    key: key,
    label: label,
    expertise: textOf(source.expertise),
    limits: textOf(source.limits),
    entry: areaEntry(source),
    asOf: textOf(source.asOf) || null,
    coverage: coverageSummary(source.coverage, labels),
    primary: primary,
    others: others,
    metrics: metrics,
    askRoute: askRoute(key, areaQuestion(label, settings.filters)),
  };
};

export const shortList = (rows, expanded) => {
  const list = listOf(rows);
  return expanded === true ? list : list.slice(0, AREA_SHORT_LIST);
};

export const toggleText = (count, expanded) => (expanded ? 'Mostrar menos' : 'Mostrar as ' + formatInteger(count) + ' métricas');

export const coverageTable = (cards) => {
  const byShard = {};
  const order = [];
  listOf(cards).forEach((card) => {
    listOf(objectOf(objectOf(card).coverage).rows).forEach((row) => {
      if (!row.shardCode) return;
      if (!byShard[row.shardCode]) {
        byShard[row.shardCode] = { key: row.shardCode, shardCode: row.shardCode, companies: 0, cells: {} };
        order.push(row.shardCode);
      }
      const entry = byShard[row.shardCode];
      entry.companies = Math.max(entry.companies, row.companies);
      entry.cells[card.key] = row;
    });
  });
  return order.sort().map((shard) => {
    const entry = byShard[shard];
    return Object.assign(entry, {
      companiesText: formatInteger(entry.companies),
      list: AREA_KEYS.map((areaKey) => Object.assign({ areaKey: areaKey }, entry.cells[areaKey] || MISSING_CELL)),
    });
  });
};

export const coverageCellOf = (row, areaKey) => {
  const cells = objectOf(objectOf(row).cells);
  return Object.prototype.hasOwnProperty.call(cells, areaKey) ? cells[areaKey] : null;
};

const coverageCellText = (row, areaKey) => {
  const cell = coverageCellOf(row, areaKey);
  return cell ? cell.text : COVERAGE_STATUS.missing.label;
};

export const areasView = (data, options) => {
  const source = objectOf(data);
  const settings = objectOf(options);
  const labels = metricLabels(source.areas);
  const notStarted = source.state === 'na' || !textOf(source.snapshotDate);
  const cards = listOf(source.areas).map((area) => areaCard(area, labels, settings));
  const metricRows = cards.reduce((all, card) => all.concat(card.metrics), []);
  return {
    notStarted: notStarted,
    stateReason: textOf(source.stateReason),
    snapshotDate: textOf(source.snapshotDate) || null,
    asOf: textOf(source.asOf) || null,
    asOfText: asOfText(source.asOf),
    snapshotLine: snapshotLine(source.snapshotDate, source.asOf),
    previous7Date: textOf(source.previous7Date) || null,
    previous28Date: textOf(source.previous28Date) || null,
    firstDate: textOf(source.firstDate) || null,
    cards: notStarted ? [] : cards,
    metricRows: notStarted ? [] : metricRows,
    coverageRows: notStarted ? [] : coverageTable(cards),
  };
};

const cutLabel = (dimension, key) => {
  if (dimension === 'sizeBand') return key ? optionLabel(SIZE_BAND_FILTER_OPTIONS, key) : 'Sem porte';
  return key || 'Sem perfil';
};

export const cutRows = (list, dimension, hasFeature) => listOf(list)
  .map((raw, index) => {
    const source = objectOf(raw);
    const cut = textOf(source.key);
    const total = numberOrNull(source.value);
    const companies = numberOrNull(source.companies);
    const eligibleCompanies = hasFeature ? numberOrNull(source.eligibleCompanies) : null;
    const companiesWithFeature = hasFeature ? numberOrNull(source.companiesWithFeature) : null;
    const share = hasFeature ? numberOrNull(source.share) : null;
    return {
      key: dimension + ':' + (cut || '-') + ':' + index,
      cut: cut,
      label: cutLabel(dimension, cut),
      total: total,
      companies: companies,
      eligibleCompanies: eligibleCompanies,
      companiesWithFeature: companiesWithFeature,
      share: share,
      totalText: total === null ? EMPTY : formatInteger(total),
      companiesText: companies === null ? EMPTY : formatInteger(companies),
      eligibleText: eligibleCompanies === null ? EMPTY : formatInteger(eligibleCompanies),
      shareText: share === null ? EMPTY : formatPercent(share),
      shareSub: companiesWithFeature !== null && eligibleCompanies !== null
        ? formatInteger(companiesWithFeature) + ' de ' + formatInteger(eligibleCompanies) : '',
    };
  })
  .sort((a, b) => {
    if (!a.cut && !b.cut) return 0;
    if (!a.cut) return 1;
    if (!b.cut) return -1;
    return a.cut.localeCompare(b.cut, 'pt-BR');
  });

export const seriesRows = (list) => listOf(list)
  .map((raw) => {
    const source = objectOf(raw);
    return { key: textOf(source.date), date: textOf(source.date), value: numberOrNull(source.value), companies: numberOrNull(source.companies) };
  })
  .filter((row) => /^\d{4}-\d{2}-\d{2}/.test(row.date))
  .map((row) => Object.assign(row, { date: row.date.substring(0, 10), key: row.date.substring(0, 10) }))
  .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

export const seriesChart = (rows, label) => [{
  key: 'value',
  label: textOf(label) || 'Total',
  area: true,
  points: listOf(rows).map((row) => ({ date: row.date, value: row.value, partial: false })),
}];

export const seriesExtras = (rows) => {
  const byDate = {};
  listOf(rows).forEach((row) => {
    byDate[row.date] = row;
  });
  return (date) => {
    const row = byDate[textOf(date).substring(0, 10)];
    return row && isNumber(row.companies) ? [{ label: 'Empresas com valor', text: formatInteger(row.companies) }] : [];
  };
};

export const seriesSummary = (rows) => {
  const list = listOf(rows);
  if (!list.length) return '';
  const first = list[0].date;
  const last = list[list.length - 1].date;
  const points = plural(list.length, 'retrato', 'retratos');
  return first === last ? points + ' (' + formatDateFull(first) + ')' : points + ' de ' + formatDateFull(first) + ' a ' + formatDateFull(last);
};

export const metricDetailView = (data, labels, options) => {
  const source = objectOf(data);
  const settings = objectOf(options);
  const areaKey = textOf(source.areaKey);
  const areaLabel = textOf(source.areaLabel) || AREA_LABELS[areaKey] || areaKey;
  const metric = metricRow(source.metric, { key: areaKey, label: areaLabel }, labels);
  const notStarted = !textOf(source.snapshotDate);
  const series = seriesRows(source.series);
  return {
    notStarted: notStarted,
    areaKey: areaKey,
    areaLabel: areaLabel,
    snapshotDate: textOf(source.snapshotDate) || null,
    asOf: textOf(source.asOf) || null,
    asOfText: asOfText(source.asOf),
    snapshotLine: snapshotLine(source.snapshotDate, source.asOf),
    previous7Date: textOf(source.previous7Date) || null,
    previous28Date: textOf(source.previous28Date) || null,
    metric: metric,
    coverage: coverageSummary(source.coverage, labels),
    series: series,
    seriesText: seriesSummary(series),
    chart: seriesChart(series, metric.label),
    bySizeBand: cutRows(source.bySizeBand, 'sizeBand', metric.hasFeature),
    bySegment: cutRows(source.bySegment, 'segment', metric.hasFeature),
    censored: numberOrNull(source.censoredCompanies) || 0,
    askRoute: askRoute(areaKey, metricQuestion(metric.label, settings.filters)),
  };
};

export const previousText = (previous, date) => {
  if (!isNumber(previous)) return 'Sem retrato para comparar';
  const day = formatDateFull(date);
  return 'antes: ' + formatInteger(previous) + (day === EMPTY ? '' : ' em ' + day);
};

export const metricStats = (metric, dates) => {
  const source = objectOf(metric);
  const days = objectOf(dates);
  const stats = [
    { key: 'value', label: 'Total', infoKey: 'areas.total', text: source.valueText || EMPTY, sub: '' },
    { key: 'companies', label: 'Empresas', infoKey: 'areas.companies', text: source.companiesText || EMPTY, sub: '' },
  ];
  if (source.hasFeature) {
    stats.push({ key: 'share', label: 'Participação', infoKey: 'areas.share', text: source.shareText || EMPTY, sub: source.shareSub || '' });
  }
  stats.push({ key: 'delta7', label: 'Δ 7 dias', infoKey: 'areas.delta7', text: source.delta7 || EMPTY, sub: previousText(source.previous7, days.previous7Date) });
  stats.push({ key: 'delta28', label: 'Δ 28 dias', infoKey: 'areas.delta28', text: source.delta28 || EMPTY, sub: previousText(source.previous28, days.previous28Date) });
  if (source.rate) stats.push({ key: 'rate', label: 'Taxa', infoKey: 'areas.rate', text: source.rateText || EMPTY, sub: source.rateSub || '' });
  return stats;
};

export const siblingLinks = (data, currentKey, query, journey) => {
  const area = objectOf(listOf(objectOf(data).areas)[0]);
  const areaKey = textOf(area.key);
  const areaInfo = { key: areaKey, label: textOf(area.label) || AREA_LABELS[areaKey] || areaKey };
  const labels = metricLabels([area]);
  return listOf(area.metrics)
    .map((raw) => {
      const source = objectOf(raw);
      const key = textOf(source.key);
      return {
        key: key,
        label: textOf(source.label) || key,
        valueText: isNumber(source.value) ? formatInteger(source.value) : EMPTY,
        current: key === currentKey,
        route: metricRoute(key, query, journey),
        entry: metricRow(source, areaInfo, labels).entry,
      };
    })
    .filter((link) => !!link.route);
};

const companyText = (row) => textOf(objectOf(row).name) || textOf(objectOf(row).companyId);

const sizeBandText = (value) => {
  const key = textOf(value);
  return SIZE_BAND.test(key) ? optionLabel(SIZE_BAND_FILTER_OPTIONS, key) : '';
};

export const areaCompanyRow = (raw, index) => {
  const source = objectOf(raw);
  const value = numberOrNull(source.value);
  const numerator = numberOrNull(source.numerator);
  const denominator = numberOrNull(source.denominator);
  const share = numberOrNull(source.share);
  const profile = [sizeBandText(source.sizeBand), textOf(source.segment) ? 'Perfil ' + textOf(source.segment) : '']
    .filter((part) => !!part)
    .join(' · ');
  return {
    key: textOf(source.companyId) + ':' + textOf(source.shardCode) + ':' + (isNumber(index) ? index : 0),
    rank: numberOrNull(source.rank),
    rankText: isNumber(source.rank) ? formatInteger(source.rank) : EMPTY,
    companyId: textOf(source.companyId),
    shardCode: textOf(source.shardCode).toUpperCase(),
    name: companyText(source),
    profileText: profile || EMPTY,
    journeyText: journeyLabel(source.clientJourney),
    trial: source.isTrial === true,
    featureText: source.featureEnabled === true ? 'Sim' : 'Não',
    valueText: value === null ? EMPTY : formatInteger(value),
    numeratorText: numerator === null ? EMPTY : formatInteger(numerator),
    denominatorText: denominator === null ? EMPTY : formatInteger(denominator),
    shareText: share === null ? EMPTY : formatPercent(share),
  };
};

export const companiesLead = (payload, formula) => {
  const source = objectOf(payload);
  const parts = [source.hasParts === true
    ? 'Empresas do recorte com valor ou com denominador no último retrato.'
    : 'Empresas do recorte com valor no último retrato.'];
  if (source.hasParts === true && textOf(formula)) parts.push('Taxa da empresa = ' + textOf(formula) + '.');
  const censored = numberOrNull(source.censoredCompanies) || 0;
  if (censored > 0) {
    parts.push(plural(censored, 'empresa', 'empresas') + ' com menos de ' + SENSITIVE_MINIMUM
      + ' na base ' + (censored === 1 ? 'ficou' : 'ficaram') + ' fora da lista e de todos os números (métrica sensível).');
  }
  return parts.join(' ');
};

export const nextAreaSort = (current, column) => {
  const state = objectOf(current);
  const key = textOf(objectOf(column).sort) || textOf(objectOf(column).key);
  if (state.sort === key) return { sort: key, desc: !state.desc };
  return { sort: key, desc: objectOf(column).startDescending !== false };
};

export const validAreaSort = (sort, hasParts) => {
  const source = objectOf(sort);
  if (source.sort === 'share' && hasParts === true) return { sort: 'share', desc: source.desc !== false };
  if (source.sort === 'value') return { sort: 'value', desc: source.desc !== false };
  return Object.assign({}, DEFAULT_AREA_COMPANY_SORT);
};

export const sortLine = (columns, sort) => {
  const state = objectOf(sort);
  const column = listOf(columns).filter((item) => (item.sort || item.key) === state.sort)[0];
  return column ? 'Ordem: ' + column.label + (state.desc ? ', decrescente' : ', crescente') : '';
};

const FEATURE_KEYS = ['eligibleCompanies', 'companiesWithFeature', 'share'];

const PART_KEYS = ['numerator', 'denominator', 'share'];

export const cutColumnsFor = (specs, hasFeature) => listOf(specs).filter((spec) => hasFeature === true || FEATURE_KEYS.indexOf(spec.key) === -1);

export const companyColumnsFor = (specs, hasFeature, hasParts) => listOf(specs).filter((spec) => {
  if (spec.key === 'featureEnabled') return hasFeature === true;
  if (PART_KEYS.indexOf(spec.key) !== -1) return hasParts === true;
  return true;
});

const rateValue = (field) => (row) => {
  const rate = objectOf(row).rate;
  return rate && typeof rate === 'object' ? rate[field] : null;
};

export const AREA_METRIC_COLUMNS = defineColumns([
  { key: 'areaLabel', label: 'Área', type: 'text', infoKey: 'areas.area', screen: false, width: 30 },
  { key: 'key', label: 'Chave da métrica', type: 'text', infoKey: 'areas.metricKey', screen: false, width: 34 },
  { key: 'label', label: 'Métrica', type: 'text', infoKey: 'areas.metric', width: 46 },
  { key: 'primary', label: 'Métrica principal', type: 'text', infoKey: 'areas.primary', screen: false },
  { key: 'value', label: 'Total', type: 'int', infoKey: 'areas.total' },
  { key: 'companies', label: 'Empresas', type: 'int', infoKey: 'areas.companies' },
  { key: 'delta7', label: 'Δ 7 dias', type: 'text', infoKey: 'areas.delta7', numeric: true, export: false, exportedAs: ['previous7', 'deltaPct7'] },
  { key: 'eligibleCompanies', label: 'Empresas que contrataram', type: 'int', infoKey: 'areas.eligible', screen: false },
  { key: 'companiesWithFeature', label: 'Com valor entre as que contrataram', type: 'int', infoKey: 'areas.companiesWithFeature', screen: false },
  { key: 'share', label: 'Participação', type: 'percent', infoKey: 'areas.share', screen: false },
  { key: 'previous7', label: 'Total 7 dias antes', type: 'int', infoKey: 'areas.previous7', screen: false },
  { key: 'deltaPct7', label: 'Variação em 7 dias', type: 'percent', infoKey: 'areas.delta7', screen: false },
  { key: 'previous28', label: 'Total 28 dias antes', type: 'int', infoKey: 'areas.previous28', screen: false },
  { key: 'deltaPct28', label: 'Variação em 28 dias', type: 'percent', infoKey: 'areas.delta28', screen: false },
  { key: 'rate', label: 'Taxa', type: 'percent', infoKey: 'areas.rate', screen: false, value: rateValue('value') },
  { key: 'rateNumerator', label: 'Numerador da taxa', type: 'int', infoKey: 'areas.numerator', screen: false, value: rateValue('numerator') },
  { key: 'rateDenominator', label: 'Denominador da taxa', type: 'int', infoKey: 'areas.denominator', screen: false, value: rateValue('denominator') },
  { key: 'state', label: 'Estado', type: 'text', infoKey: 'areas.state', screen: false, value: (row) => objectOf(row).stateLabel },
  { key: 'stateReason', label: 'Motivo do estado', type: 'text', infoKey: 'areas.stateReason', screen: false, width: 50 },
  { key: 'asOf', label: 'Dados de', type: 'datetime', infoKey: 'areas.asOf', screen: false },
  { key: 'definition', label: 'Definição', type: 'text', infoKey: 'areas.definition', screen: false, width: 80 },
]);

export const AREA_COVERAGE_COLUMNS = defineColumns([
  { key: 'shardCode', label: 'Banco', type: 'text', infoKey: 'common.shard' },
  { key: 'companies', label: 'Empresas no recorte', type: 'int', infoKey: 'areas.coverageCompanies' },
].concat(AREA_KEYS.map((areaKey) => ({
  key: areaKey,
  label: AREA_LABELS[areaKey],
  type: 'text',
  infoKey: 'areas.coverageStatus',
  width: 28,
  value: (row) => coverageCellText(row, areaKey),
}))));

export const AREA_SERIES_COLUMNS = defineColumns([
  { key: 'date', label: 'Data do retrato', type: 'date', infoKey: 'areas.snapshotDate' },
  { key: 'value', label: 'Total', type: 'int', infoKey: 'areas.total' },
  { key: 'companies', label: 'Empresas', type: 'int', infoKey: 'areas.companies' },
]);

const CUT_COLUMNS = [
  { key: 'total', label: 'Total', type: 'int', infoKey: 'areas.total' },
  { key: 'companies', label: 'Empresas', type: 'int', infoKey: 'areas.companies' },
  { key: 'eligibleCompanies', label: 'Empresas que contrataram', type: 'int', infoKey: 'areas.eligible' },
  { key: 'companiesWithFeature', label: 'Com valor entre as que contrataram', type: 'int', infoKey: 'areas.companiesWithFeature', screen: false },
  { key: 'share', label: 'Participação', type: 'percent', infoKey: 'areas.share' },
];

export const AREA_SIZE_CUT_COLUMNS = defineColumns([{ key: 'label', label: 'Porte', type: 'text', infoKey: 'common.sizeBand', width: 30 }].concat(CUT_COLUMNS));

export const AREA_SEGMENT_CUT_COLUMNS = defineColumns([{ key: 'label', label: 'Perfil da empresa', type: 'text', infoKey: 'common.segment', width: 30 }]
  .concat(CUT_COLUMNS));

export const AREA_COMPANY_COLUMNS = defineColumns([
  { key: 'rank', label: '#', type: 'int', infoKey: 'areas.rank' },
  { key: 'name', label: 'Empresa', type: 'text', infoKey: 'common.company', width: 40, value: companyText },
  { key: 'companyId', label: 'Identificador da empresa', type: 'text', infoKey: 'common.companyId', screen: false, width: 34 },
  { key: 'shardCode', label: 'Banco', type: 'text', infoKey: 'common.shard', screen: false },
  {
    key: 'profile', label: 'Porte, perfil e jornada', type: 'text', infoKey: 'areas.profile', export: false,
    exportedAs: ['sizeBand', 'segment', 'clientJourney', 'isTrial'],
  },
  { key: 'sizeBand', label: 'Porte', type: 'text', infoKey: 'common.sizeBand', screen: false, value: (row) => sizeBandText(objectOf(row).sizeBand) },
  { key: 'segment', label: 'Perfil da empresa', type: 'text', infoKey: 'common.segment', screen: false },
  { key: 'clientJourney', label: 'Jornada', type: 'text', infoKey: 'common.journey', screen: false, value: (row) => journeyLabel(objectOf(row).clientJourney) },
  { key: 'isTrial', label: 'Em trial', type: 'text', infoKey: 'areas.isTrial', screen: false, value: (row) => objectOf(row).isTrial === true },
  { key: 'featureEnabled', label: 'Contratou', type: 'text', infoKey: 'areas.featureEnabled', value: (row) => objectOf(row).featureEnabled === true },
  { key: 'value', label: 'Valor', type: 'int', infoKey: 'areas.companyValue', sortable: true, sort: 'value', startDescending: true },
  { key: 'numerator', label: 'Numerador', type: 'int', infoKey: 'areas.numerator' },
  { key: 'denominator', label: 'Denominador', type: 'int', infoKey: 'areas.denominator' },
  { key: 'share', label: 'Taxa da empresa', type: 'percent', infoKey: 'areas.companyRate', sortable: true, sort: 'share', startDescending: true },
]);



// WEBPACK FOOTER //
// ./src/vue/pages/components/pi/piAreasModel.js